import React, { useRef, useEffect, useMemo } from 'react'
import { IconS24Plus24 } from '@is24/cosma-ui-icons'
import { Uploader } from 'plupload'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../../../../../../../../../app/hooks'
import { API_ENDPOINT, ATTACHMENT_NUMBER_LIMIT, BUILD_NUMBER } from '../../../../../../../../../../../config'
import { AttachmentType } from '../../../../../../../../../../../@types/types'
import { UploadFile, MultimediaUploaderType } from '../../../../../../../../../../../@types/multimedia-types'
import {
  startUploadAttachmentRequest,
  progressUploadAttachment,
} from '../../../../../../../../../../../redux/reducers/multimedia/multimediaFeature'
import { finishUpload } from '../../../../../../../../../../../redux/thunks/multimedia/multimedia'
import { showMultimediaMessage } from '../../../../../../../../../../../redux/reducers/message/messageFeature'
import { trackingOnAttachmentUpload } from '../../../../../../../../../../../tracking/tracking'
import { filenameIsValid } from '../../../../../../../../../../../services/validation/rules/attachmentFileNameRule'
import styles from './AddPictureButton.module.css'
import { getDefaultLogger } from '../../../../../../../../../../../services/logger/logger'
import { UPLOAD_ATTEMPT_ID_HEADER } from '../../../../../../../../../../../api/multimedia/uploadAttemptId'

export interface AddPictureButtonProps {
  onClick?: () => void
  flowInformation?: any
  allowedFileType?: MultimediaUploaderType
  entryMode?: string
}

export const AddPictureButton: React.FC<AddPictureButtonProps> = ({
  flowInformation,
  allowedFileType = MultimediaUploaderType.PICTURE,
  entryMode,
}) => {
  const uploaderRef = useRef<Uploader | null>(null)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  useMemo(() => uuid(), [])
  const attachmentsNumber = useAppSelector((state: any) => state.multimediaFeature.attachments?.length || 0)

  const initializeUploader = (buttonElement: HTMLButtonElement) => {
    const uploader: Uploader = new Uploader({
      runtimes: 'html5,html4',
      browse_button: buttonElement,
      url: API_ENDPOINT + '/anbieten/private-anbieter/inserieren/api/multimedia/upload',
      file_data_name: 'file',
      init: {
        FilesAdded: (up, files) => {
          if (files?.length + attachmentsNumber >= ATTACHMENT_NUMBER_LIMIT) {
            files.forEach((file) => up.removeFile(file))
            dispatch(showMultimediaMessage({ message: 'attachmentsNumber' }))
            return
          }
          const invalidFileName = files.some((file) => !filenameIsValid(file.name))
          if (invalidFileName) {
            dispatch(showMultimediaMessage({ message: 'attachmentFilename' }))
            files.forEach((file) => {
              up.removeFile(file)
            })
            return
          }

          files.forEach((file: UploadFile) => {
            dispatch(startUploadAttachmentRequest({ attachmentType: AttachmentType.PICTURE, file: file }))
          })
          if (flowInformation && allowedFileType && entryMode) {
            trackingOnAttachmentUpload(flowInformation, allowedFileType, entryMode)
          }
          up.start()
          up.refresh()
        },
        UploadProgress: (up, file) => {
          dispatch(progressUploadAttachment({ fileId: file.id, percent: file.percent }))
        },
        BeforeUpload: (up, file) => {
          up.setOption('headers', { [UPLOAD_ATTEMPT_ID_HEADER]: file.id })
        },
        PostInit: () => uploader.refresh(),
        FileUploaded: (up, file, response) => {
          dispatch(finishUpload(file, response))
        },
        Error: (up, error) => {
          getDefaultLogger().warn('MULTIMEDIA_UPLOAD_FAILED', {
            allowedFileType,
            buildNumber: BUILD_NUMBER,
            entryMode,
            errorCode: error.code,
            errorMessage: error.message,
            fileId: error.file?.id,
            fileName: error.file?.name,
            filePercent: error.file?.percent,
            fileSize: error.file?.size,
            fileStatus: error.file?.status,
            fileType: error.file?.type,
            flowName: flowInformation?.name,
            httpStatus: (error as any).status,
            queuedFileCount: (up as any).files?.length,
            runtime: (up as any).runtime,
            uploadClient: 'plupload',
            uploadAttemptId: error.file?.id,
            uploaderState: (up as any).state,
          })
          if (error.code === -600)
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileSize',
                values: {
                  filename: error.file.name,
                },
              })
            )
          else if (error.code === -601) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileType',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else {
            dispatch(showMultimediaMessage({ message: 'attachment' }))
          }
        },
      },
      multi_selection: true,
      drop_element: 'add-picture-button',
      filters: {
        max_file_size: '50mb',
        mime_types: [
          {
            extensions: allowedFileType.toString(),
            title: t('wizard.screen.multimedia.supportedFiles', 'Supported Files'),
          },
        ],
      },
      multipart_params: {
        isFloorPlan: false,
      },
    })

    uploader.init()
    uploader.refresh()
    uploaderRef.current = uploader
  }

  const buttonRef = (buttonElement: HTMLButtonElement | null) => {
    if (buttonElement && !uploaderRef.current) {
      initializeUploader(buttonElement)
    }
  }

  useEffect(() => {
    return () => {
      if (uploaderRef.current) {
        try {
          uploaderRef.current.destroy()
        } catch (error) {
          getDefaultLogger().warn('MULTIMEDIA_UPLOADER_DESTROY_FAILED', {
            buildNumber: BUILD_NUMBER,
            errorMessage: error instanceof Error ? error.message : String(error),
          })
        }
        uploaderRef.current = null
      }
    }
  }, [])

  return (
    <button
      ref={buttonRef}
      id='add-picture-button'
      className={`${styles.addPictureButton} display-flex align-items-center justify-content-center cursor-pointer padding-none margin-none`}
      type='button'
    >
      <IconS24Plus24 className='color-text-weak' />
    </button>
  )
}
